import {
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Eye, EyeOff } from "lucide-react";
import { SecretFormHandle, SecretFormProps } from "../types";
import DragNDrop from "../../common/DragNDrop";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useUploader } from "../../creation/FileUploader";
import { PlainMessage } from "@bufbuild/protobuf";
import { UploadAttachment } from "protogen/common_pb";
import AttachmentList from "../../editor/AttachmentList";
import UploadDocumentsButton from "../UploadDocumentsButton";

export default forwardRef<SecretFormHandle, SecretFormProps>(
  (
    { secret, updateFormData, errors, onUploadChange, isEdit }: SecretFormProps,
    ref,
  ) => {
    const [dragState, setDragState] = useState(false);
    const [showDescription, setShowDescription] = useState(!isEdit);
    const {
      onUpload,
      fileUploads,
      uploadPercentage,
      removeUpload,
      uploadsInProgress,
      withAttachments,
    } = useUploader({
      initialAttachments: secret.attachments || [],
      sseRef: "client-secrets",
    });
    useEffect(() => {
      withAttachments(secret.attachments || []);
    }, [secret.attachments]);

    useImperativeHandle(ref, () => ({
      isValid: () => {
        const notUploading = !uploadsInProgress || uploadPercentage === 100;
        const hasName = secret.name !== "";

        return notUploading && hasName;
      },
    }));

    useEffect(() => {
      const notUploading = !uploadsInProgress || uploadPercentage === 100;
      onUploadChange && onUploadChange(!notUploading);
    }, [uploadsInProgress, uploadPercentage]);

    // Use the useCallback here because draft can change while we are uploading so we need to make sure it isn't stale.
    // Upon further reflection, I'm not sure I need useCallback nor if the state I'm listening to is relevant. This
    // is a duct tape solution that seems to be fine.
    const setAttachments = useCallback(
      (attachments: PlainMessage<UploadAttachment>[]) => {
        // Note - there could be a race condition here if the user is uploading a file AND updating text fields.
        updateFormData({ ...secret, uploads: attachments });
      },
      [secret, fileUploads],
    );

    return (
      <DragNDrop
        setHover={setDragState}
        onUpload={(files) => {
          onUpload(files, undefined, setAttachments);
        }}
      >
        <Box
          sx={{
            ...(dragState ? { backgroundColor: "#e8f4f8" } : {}),
          }}
        >
          <TextField
            margin="dense"
            label="Title"
            type="text"
            fullWidth
            variant="outlined"
            value={secret.name}
            error={!!errors?.value}
            helperText={errors?.value}
            onChange={(e) =>
              updateFormData({ ...secret, name: e.target.value })
            }
          />
          <TextField
            sx={{ userSelect: "none" }} // This needs to be here for InputProps to work, it gets overriten by state
            minRows={5}
            multiline
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            value={secret.value || ""}
            error={!!errors?.value}
            helperText={errors?.value}
            onChange={(e) =>
              updateFormData({ ...secret, value: e.target.value })
            }
            disabled={!showDescription}
            onFocus={(e) => {
              if (!showDescription) {
                e.preventDefault();
              }
            }}
            InputProps={{
              sx: {
                "& textarea": {
                  filter: showDescription ? "none" : "blur(5px)",
                  transition: "filter 0.3s ease-in-out",
                  ...(!showDescription
                    ? { userSelect: "none", pointerEvents: "none" } // Need pointEvents for userSelect to work
                    : {}),
                },
              },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowDescription((prev) => !prev)}
                  >
                    {showDescription ? <EyeOff /> : <Eye />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box
            display="flex"
            flexDirection="column"
            gap="10px"
            sx={{ marginTop: "24px" }}
          >
            <Typography variant="bodyHeavy">Documents</Typography>
            <AttachmentList
              attachments={fileUploads}
              onDelete={(filename) => removeUpload(filename, setAttachments)}
            />
            <UploadDocumentsButton
              uploadsInProgress={uploadsInProgress}
              uploadPercentage={uploadPercentage}
              onChange={(files) => onUpload(files, undefined, setAttachments)}
            />
          </Box>
        </Box>
      </DragNDrop>
    );
  },
);
